import React, { useEffect } from 'react';
import Table from './Table';
import { Grid } from 'semantic-ui-react';
import Loader from 'components/Loader/Loader';
import UpliftBox from './UpliftBox';
import MinimumGuaranteeBox from '../MinimumGuaranteeBox/MinimumGuaranteeBox';
import useFilters from 'hooks/useFilters';
import { Organization, SpotInterface } from 'types';
import { Push } from 'connected-react-router';
import { getFormattedDateYYYY_MM_DD, isValidDateForIncomeStatement } from 'utils/helpers';
import AGSimpleSelect from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import LinesIcon from 'assets/icons/lines.svg?react';
import { DateRangePicker, formatISODate } from 'shared-ui';
import {
    getDateRangeKey,
    getLastMonthRange,
    getOrgLaunchDate,
    getSingleMonthLabel,
    getSpotSelectOptions,
    INCOME_STATEMENT_PRESETS,
} from './utils';
import DownloadStatement from './DownloadStatement';
import styles from './IncomeStatement.module.css';
import { useIncomeStatement } from 'contexts/IncomeStatementContext';
import AGMessage from 'components/AGMessage/AGMessage';

type IncomeStatementProps = {
    connectedPush: Push;
    orgSpots: Record<string, SpotInterface>;
    organization: Organization;
};

const IncomeStatement = ({ connectedPush, orgSpots, organization }: IncomeStatementProps) => {
    const { getIncomeStatement, loading, errorMessage, incomeStatements } = useIncomeStatement();
    const selectOptions = getSpotSelectOptions(orgSpots);
    const today = new Date();
    const lastFullMonth = getLastMonthRange(today);
    const launchDate = getOrgLaunchDate(orgSpots);

    const { filters, urlUpdater } = useFilters<{ spotPk: string; startDate: string; endDate: string }>({
        filterValidators: {
            spotPk: function spotPkValidator(currentValue: string | null): string {
                const spots = Object.keys(orgSpots);
                const isOneSpotOrg = spots.length === 1;
                return currentValue && orgSpots && orgSpots[currentValue] ? currentValue : isOneSpotOrg ? spots[0] : '';
            },
            startDate: function dateValidator(currentValue: string | null): string {
                return isValidDateForIncomeStatement(currentValue)
                    ? currentValue
                    : getFormattedDateYYYY_MM_DD(lastFullMonth.startDate);
            },
            endDate: function dateValidator(currentValue: string | null): string {
                return isValidDateForIncomeStatement(currentValue)
                    ? currentValue
                    : getFormattedDateYYYY_MM_DD(lastFullMonth.endDate);
            },
        },
        push: connectedPush,
    });

    let report;
    const dateRange = getDateRangeKey(filters.startDate, filters.endDate);
    const completeReport = incomeStatements[dateRange];
    if (completeReport) {
        if (!filters.spotPk) report = completeReport.organization;
        else report = completeReport.spots.find((report) => report.uuid === orgSpots[filters.spotPk].uuid);
    }

    useEffect(() => {
        const dateRange = getDateRangeKey(filters.startDate, filters.endDate);

        if (!incomeStatements[dateRange] && filters.startDate && filters.endDate)
            getIncomeStatement(organization.uuid, filters.startDate, filters.endDate, dateRange);
    }, [filters.startDate, filters.endDate, orgSpots, getIncomeStatement, incomeStatements, organization]);

    return (
        <div style={{ paddingBottom: '8%' }}>
            <Grid stackable>
                {!!errorMessage && (
                    <AGMessage color="error" style={{ width: '100%', marginInline: '1rem' }}>
                        {errorMessage}
                    </AGMessage>
                )}

                <div className={styles.headerOptions}>
                    {!!selectOptions && (
                        <AGSimpleSelect
                            icon={<LinesIcon />}
                            selected={filters.spotPk}
                            options={selectOptions}
                            onSelect={urlUpdater.spotPk}
                            width="250px"
                        />
                    )}
                    <DateRangePicker
                        format="date"
                        fromValue={filters.startDate}
                        toValue={filters.endDate}
                        onFromValueChange={(value) => urlUpdater.startDate(formatISODate(value) ?? '')}
                        onToValueChange={(value) => urlUpdater.endDate(formatISODate(value) ?? '')}
                        formatDateConfig={{ compact: true, timeZoneDisplay: false }}
                        containerProps={{ style: { width: '290px' } }}
                        className={styles.datePicker}
                        presets={INCOME_STATEMENT_PRESETS}
                        presetsOrientation={'left'}
                        disabledDates={launchDate ? [{ before: launchDate }, { after: today }] : { after: today }}
                        startMonth={launchDate}
                        endMonth={today}
                    />
                    <DownloadStatement
                        propertyOptions={selectOptions}
                        {...filters}
                        orgSpots={orgSpots}
                        orgUuid={organization.uuid}
                    />
                </div>
                <Grid.Row>
                    {!report || loading ? (
                        <Loader centered />
                    ) : (
                        <>
                            <Grid.Column computer={10} tablet={16}>
                                <Table report={report} startDate={filters.startDate} endDate={filters.endDate} />
                            </Grid.Column>
                            <Grid.Column computer={6} tablet={16}>
                                <UpliftBox report={report} />
                                {!!report.income_statement.minimum_guarantee_amount && (
                                    <MinimumGuaranteeBox
                                        netRevenue={report.income_statement.net_income}
                                        minimumGuaranteeAmount={report.income_statement.minimum_guarantee_amount}
                                        monthLabel={getSingleMonthLabel(filters.startDate, filters.endDate)}
                                    />
                                )}
                            </Grid.Column>
                        </>
                    )}
                </Grid.Row>
            </Grid>
        </div>
    );
};

export default IncomeStatement;
